/**
 * Cockpit's half of the help slide-out.
 *
 * The panel itself is the shared `HelpPanel` from `@ampersandbrands/ui`, so
 * Cockpit and The Sweet Spot show the same thing. This file supplies the parts
 * that are Cockpit's: a client that asks for the Cockpit audience (everything
 * except store-only articles), the article to open directly (resolved from the
 * tool catalog for whatever page the reader is on), and the "Improve this
 * article" feedback box, gated by the `admin.kb-feedback` accessSlug (spec
 * 00_access-binary-grants.md; console-only catalog row `admin-kb-feedback` in
 * shared/catalog.ts, no sidebar presence, no route).
 */

import { useLocation } from 'react-router-dom';
import { HelpPanel as SharedHelpPanel, type HelpClient } from '@ampersandbrands/ui';
import { COCKPIT_DEPARTMENTS } from '@/data/tools.config';
import { activeToolForPath } from '@/lib/toolRouting';
import { apiGet, apiPost, API_BASE } from '@/lib/api';
import { useSession } from '@/lib/useSession';
import { hasAccessSlug } from '@/lib/toolVisibility';

const HELP_BASE = `${API_BASE}/v1/core/help`;

/**
 * Help articles for pages that are not catalog tools, keyed by exact path.
 * Catalog tools carry their own `helpKbPageId` in shared/catalog.ts; the home
 * page (and other shell pages) have no tool entry, so without this the panel
 * opened on the index instead of the page's own article.
 */
const NON_TOOL_HELP_PAGES: Record<string, string> = {
  '/': '147521537', // The Cockpit Home Page Sales Dashboard
  '/settings': '150011909', // Notifications in Cockpit
  '/reports': '165314561', // Using the Reports Page in Cockpit
};

const cockpitHelpClient: HelpClient = {
  async listArticles() {
    // No audience parameter: the API defaults to Cockpit, which is everything
    // except the store-only articles.
    const res = await apiGet<{ articles: { id: string; title: string; category: string }[] }>(
      `${HELP_BASE}/articles`,
    );
    return res.articles ?? [];
  },
  async getArticle(pageId: string) {
    const article = await apiGet<{ id: string; title: string; html: string; portal_url?: string }>(
      `${HELP_BASE}/articles/${encodeURIComponent(pageId)}`,
    );
    // The API serves article images as root-relative paths and the UI is on a
    // different origin, so they need the API host prefixed or they 404.
    return {
      ...article,
      html: article.html.replaceAll('src="/v1/core/help/', `src="${API_BASE}/v1/core/help/`),
    };
  },
};

export function HelpPanel({
  open,
  onClose,
  onReportIssue,
}: {
  open: boolean;
  onClose: () => void;
  onReportIssue?: () => void;
}) {
  const location = useLocation();
  const { user } = useSession();
  const activeTool = activeToolForPath(location.pathname);
  let initialPageId: string | null = NON_TOOL_HELP_PAGES[location.pathname] ?? null;
  if (!initialPageId && activeTool) {
    const dept = COCKPIT_DEPARTMENTS.find((d) => d.id === activeTool.deptId);
    const tool = dept?.tools.find((t) => t.id === activeTool.toolId);
    initialPageId = tool?.helpKbPageId ?? null;
  }

  const canSubmitFeedback = hasAccessSlug(user?.permissions, 'admin.kb-feedback', user?.superAdmin);

  return (
    <SharedHelpPanel
      open={open}
      onClose={onClose}
      client={cockpitHelpClient}
      initialPageId={initialPageId}
      onReportIssue={onReportIssue}
      pinnedCategory="Cockpit"
      feedback={{
        canSubmit: canSubmitFeedback,
        submit: async ({ pageId, articleTitle, instruction }) => {
          await apiPost(`${HELP_BASE}/articles/${encodeURIComponent(pageId)}/feedback`, {
            instruction,
            article_title: articleTitle,
            app: 'cockpit',
            route: location.pathname,
          });
        },
      }}
    />
  );
}
